import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Checkbox,
  DatePicker,
  InputNumber,
  Message,
  SelectPicker,
  toaster,
} from 'rsuite';
import { Button as NsiButton, Modal, sortRows, useLastPresent, useTableSort } from '@nsi-advisory-services/components';

import { useDeleteUserRate, useProjects, useUpsertUserRate, useUser, useUserRates } from '../hooks/useQueries';
import './ConfigurationSheet.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencyInputFormatter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)}`;
};

const currencyInputParser = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(/[^0-9.-]/g, '');
};

const CurrentBadge = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1px 8px',
      borderRadius: 999,
      border: '1px solid #b7eb8f',
      background: '#f6ffed',
      color: '#389e0d',
      fontSize: 11,
      fontWeight: 800,
      lineHeight: '16px',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
    }}
  >
    Current
  </span>
);

export default function UserDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const userId = Number(params?.userId);

  // A link from a project page carries its origin in the URL, so Back can name where
  // it goes instead of relying on history (which a reload or a deep link would lose).
  const backTo = useMemo(() => {
    if (searchParams.get('from') !== 'project') return null;
    const fromProjectId = Number(searchParams.get('fromProjectId'));
    if (!Number.isInteger(fromProjectId)) return null;
    return { label: 'Back to project', path: `/projects/${fromProjectId}` };
  }, [searchParams]);

  useEffect(() => {
    if (location?.hash !== '#rates') return;
    // Let the page render before attempting to scroll.
    window.setTimeout(() => {
      const el = document.getElementById('rates');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [location?.hash]);

  const { data: user, isLoading: userLoading, error: userError } = useUser(Number.isInteger(userId) ? userId : null);
  const { data: rates = [], isLoading: ratesLoading, error: ratesError } = useUserRates(
    Number.isInteger(userId) ? userId : null
  );

  const { data: projects = [], isLoading: projectsLoading, error: projectsError } = useProjects();

  const upsertRate = useUpsertUserRate({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Rate saved.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    },
  });

  const [suppressDeleteRateToast, setSuppressDeleteRateToast] = useState(false);
  const deleteRate = useDeleteUserRate({
    onSuccess: async () => {
      if (suppressDeleteRateToast) return;
      toaster.push(
        <Message showIcon type="success" closable>
          Rate removed.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    },
  });

  const [addRateOpen, setAddRateOpen] = useState(false);
  const [newProjectId, setNewProjectId] = useState(null);
  const [newRate, setNewRate] = useState(0);
  const [newEffectiveDate, setNewEffectiveDate] = useState(() => new Date());

  const [ratesProjectFilterId, setRatesProjectFilterId] = useState(null);
  const [ratesOnlyCurrent, setRatesOnlyCurrent] = useState(false);

  // Editing happens in a modal keyed on the row being edited, rather than
  // per-cell inline editors.
  const [editRateRow, setEditRateRow] = useState(null);
  const editRateRowShown = useLastPresent(editRateRow); // keeps the dialog steady while it fades out
  const [editRateDate, setEditRateDate] = useState(null);
  const [editRateValue, setEditRateValue] = useState(0);

  const normalizeIso10 = (raw) => {
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      const yyyy = raw.getFullYear();
      const mm = String(raw.getMonth() + 1).padStart(2, '0');
      const dd = String(raw.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    const s = String(raw ?? '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  const iso10ToDate = (iso10) => {
    const s = normalizeIso10(iso10);
    if (!s) return null;
    const yyyy = Number(s.slice(0, 4));
    const mm = Number(s.slice(5, 7));
    const dd = Number(s.slice(8, 10));
    if (!Number.isInteger(yyyy) || !Number.isInteger(mm) || !Number.isInteger(dd)) return null;
    return new Date(yyyy, mm - 1, dd);
  };

  const projectOptions = useMemo(
    () => (projects || []).map((p) => ({ label: p.name, value: Number(p.id) })),
    [projects]
  );

  const currentEffectiveDateByProjectId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(rates) ? rates : [];
    for (const r of rows) {
      const pid = Number(r?.projectId);
      const eff = normalizeIso10(r?.effectiveDate);
      if (!Number.isInteger(pid) || !eff) continue;
      const prev = map.get(pid);
      if (!prev || String(eff).localeCompare(String(prev)) > 0) {
        map.set(pid, eff);
      }
    }
    return map;
  }, [rates]);

  const visibleRates = useMemo(() => {
    const rows = Array.isArray(rates) ? rates : [];
    const pid = ratesProjectFilterId;
    const base = Number.isInteger(pid) ? rows.filter((r) => Number(r?.projectId) === pid) : rows;

    if (!ratesOnlyCurrent) return base;

    return base.filter((r) => {
      const projId = Number(r?.projectId);
      const eff = normalizeIso10(r?.effectiveDate);
      if (!Number.isInteger(projId) || !eff) return false;
      const currentEff = currentEffectiveDateByProjectId.get(projId);
      return Boolean(currentEff) && eff === currentEff;
    });
  }, [rates, ratesProjectFilterId, ratesOnlyCurrent, currentEffectiveDateByProjectId]);

  const openAddRate = () => {
    const preferredProjectId = Number.isInteger(ratesProjectFilterId) ? ratesProjectFilterId : null;
    setNewProjectId(preferredProjectId);
    setNewRate(0);
    setNewEffectiveDate(new Date());
    setAddRateOpen(true);
  };

  const submitAddRate = async () => {
    if (!Number.isInteger(userId)) return;
    if (!Number.isInteger(newProjectId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a project.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const normalized = Number(newRate);
    const rate = Number.isFinite(normalized) ? normalized : 0;

    const effectiveDate = normalizeIso10(newEffectiveDate);
    if (!effectiveDate) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please choose an effective date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await upsertRate.mutateAsync({ userId, projectId: newProjectId, rate, effectiveDate });
    setAddRateOpen(false);
  };

  const openEditRate = (row) => {
    const pid = Number(row?.projectId);
    const effectiveDate = normalizeIso10(row?.effectiveDate);
    if (!Number.isInteger(pid) || !effectiveDate) return;
    setEditRateRow(row);
    setEditRateDate(iso10ToDate(effectiveDate));
    setEditRateValue(Number(row?.rate ?? 0));
  };

  const closeEditRate = () => {
    setEditRateRow(null);
  };

  // Runs once the dialog has faded out (onExited), so its fields don't empty while it closes.
  const resetEditRate = () => {
    setEditRateDate(null);
    setEditRateValue(0);
  };

  // Moving a rate to a different effective date means writing the new row and
  // dropping the old one; the date is part of the record's identity.
  const saveEditRate = async () => {
    const projectId = Number(editRateRow?.projectId);
    const oldEffectiveDate = normalizeIso10(editRateRow?.effectiveDate);
    if (!Number.isInteger(userId) || !Number.isInteger(projectId) || !oldEffectiveDate) {
      closeEditRate();
      return;
    }

    const newEffectiveDate = normalizeIso10(editRateDate);
    if (!newEffectiveDate) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please choose an effective date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const normalized = Number(editRateValue);
    const rate = Number.isFinite(normalized) ? normalized : 0;

    await upsertRate.mutateAsync({ userId, projectId, rate, effectiveDate: newEffectiveDate });

    if (newEffectiveDate !== oldEffectiveDate) {
      try {
        setSuppressDeleteRateToast(true);
        await deleteRate.mutateAsync({ userId, projectId, effectiveDate: oldEffectiveDate });
      } finally {
        setSuppressDeleteRateToast(false);
      }
    }

    closeEditRate();
  };

  const deleteEditRate = async () => {
    const projectId = Number(editRateRow?.projectId);
    const effectiveDate = normalizeIso10(editRateRow?.effectiveDate);
    if (!Number.isInteger(userId) || !Number.isInteger(projectId) || !effectiveDate) return;

    try {
      await deleteRate.mutateAsync({ userId, projectId, effectiveDate });
      closeEditRate();
    } catch {
      toaster.push(
        <Message type="error" showIcon>
          Failed to remove rate.
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );
    }
  };

  const ratesSort = useTableSort('projectName');

  const sortedRates = useMemo(
    () => sortRows(visibleRates, ratesSort.sortKey, ratesSort.sortDir, {
      projectName: (r) => String(r?.projectName ?? ''),
      effectiveDate: (r) => String(r?.effectiveDate ?? ''),
      rate: (r) => Number(r?.rate ?? 0),
    }),
    [visibleRates, ratesSort.sortKey, ratesSort.sortDir]
  );

  const userDisplayName = user?.name ?? (userLoading ? 'Loading…' : 'User');

  return (
    <div className="ppc-page ppc-cfg">
      <header className="ppc-cfg__header">
        <div className="ppc-cfg__header-main">
          <div className="ppc-cfg__crumbs">
            <button
              type="button"
              className="ppc-cfg__crumb-link"
              onClick={() => navigate('/configuration')}
            >
              Configuration
            </button>
            <i className="bi bi-chevron-right ppc-cfg__crumb-sep" aria-hidden="true" />
            <button
              type="button"
              className="ppc-cfg__crumb-link"
              onClick={() => navigate('/configuration/users')}
            >
              Users
            </button>
            <i className="bi bi-chevron-right ppc-cfg__crumb-sep" aria-hidden="true" />
            <span className="ppc-cfg__crumb-current">{userDisplayName}</span>
          </div>

          <h1 className="ppc-cfg__title">
            <span className="ppc-cfg__title-row">
              <i className="bi bi-person-circle ppc-cfg__title-icon" aria-hidden="true" />
              <span>{userDisplayName}</span>
            </span>
          </h1>
        </div>

        <div className="ppc-cfg-section__actions">
          <button
            type="button"
            className="ppc-cfg-btn"
            onClick={() => (backTo ? navigate(backTo.path) : navigate(-1))}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" />
            {backTo ? backTo.label : 'Back'}
          </button>
          <button
            type="button"
            className="ppc-cfg-btn ppc-cfg-btn--primary"
            onClick={openAddRate}
            disabled={!Number.isInteger(userId)}
          >
            Add Rate
            <i className="bi bi-plus-lg" aria-hidden="true" />
          </button>
        </div>
      </header>

      {userError ? <Message type="error" showIcon>Failed to load user.</Message> : null}
      {ratesError ? <Message type="error" showIcon>Failed to load rates.</Message> : null}
      {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}

      <section className="ppc-cfg-section" id="rates">
        <div className="ppc-cfg-section__head">
          <div className="ppc-cfg-section__heading">
            <h2 className="ppc-cfg-section__title">Rates (per project)</h2>
            <span className="ppc-cfg-count">{visibleRates.length}</span>
          </div>
        </div>

        <div className="ppc-cfg-filters">
          <span className="ppc-cfg-filters__label">Project</span>
          <div className="ppc-cfg-filters__picker">
            <SelectPicker
              block
              searchable
              cleanable
              placeholder={projectsLoading ? 'Loading…' : 'All projects'}
              data={projectOptions}
              value={ratesProjectFilterId}
              onChange={(v) => {
                if (v === null || v === undefined || v === '') {
                  setRatesProjectFilterId(null);
                  return;
                }
                const n = Number(v);
                setRatesProjectFilterId(Number.isInteger(n) ? n : null);
              }}
              disabled={projectsLoading || projectOptions.length === 0}
            />
          </div>

          <Checkbox checked={ratesOnlyCurrent} onChange={(_value, checked) => setRatesOnlyCurrent(checked)}>
            Only show current
          </Checkbox>
        </div>

        <div className="nsi-table-wrap">
          <table className="nsi-table nsi-table--gridlines">
            <thead>
              <tr>
                <th scope="col" aria-sort={ratesSort.ariaSort('projectName')}>
                  <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('projectName')}>
                    Project
                    <i className={ratesSort.sortIconClass('projectName')} aria-hidden="true" />
                  </button>
                </th>
                <th scope="col" style={{ width: 170 }} aria-sort={ratesSort.ariaSort('effectiveDate')}>
                  <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('effectiveDate')}>
                    Effective
                    <i className={ratesSort.sortIconClass('effectiveDate')} aria-hidden="true" />
                  </button>
                </th>
                <th scope="col" className="nsi-table__right" style={{ width: 200 }} aria-sort={ratesSort.ariaSort('rate')}>
                  <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('rate')}>
                    Rate
                    <i className={ratesSort.sortIconClass('rate')} aria-hidden="true" />
                  </button>
                </th>
              </tr>
            </thead>

            <tbody>
              {ratesLoading ? (
                <tr>
                  <td className="nsi-table__empty" colSpan={3}>Loading rates…</td>
                </tr>
              ) : null}

              {!ratesLoading && visibleRates.length === 0 ? (
                <tr>
                  <td className="nsi-table__empty" colSpan={3}>No rates yet.</td>
                </tr>
              ) : null}

              {!ratesLoading && sortedRates.map((rowData) => {
                const pid = Number(rowData?.projectId);
                const effectiveDate = normalizeIso10(rowData?.effectiveDate);
                const rowKey = `${rowData?.userId}-${rowData?.projectId}-${String(rowData?.effectiveDate || '')}`;
                const currentEffective = Number.isInteger(pid) ? currentEffectiveDateByProjectId.get(pid) : null;
                const isCurrent = Boolean(effectiveDate && currentEffective && effectiveDate === currentEffective);

                return (
                  <tr
                    key={rowKey}
                    className="nsi-table__row"
                    onClick={() => openEditRate(rowData)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openEditRate(rowData);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    title="Edit this rate"
                  >
                    <td>{rowData?.projectName}</td>
                    <td className="nsi-table__num">{rowData?.effectiveDate || '—'}</td>
                    <td className="nsi-table__right nsi-table__num">
                      <span className="ppc-cfg-ratecell">
                        {isCurrent ? <CurrentBadge /> : null}
                        {currencyFormatter.format(Number(rowData?.rate ?? 0))}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <Modal open={Boolean(editRateRow)} onClose={closeEditRate} onExited={resetEditRate} size="sm">
        <Modal.Header>
          <Modal.Title>Edit Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div className="ppc-cfg-setting__label">Project</div>
              <div className="ppc-cfg-setting__name">{editRateRowShown?.projectName ?? '—'}</div>
            </div>

            <div>
              <div className="ppc-cfg-setting__label">Effective date</div>
              <DatePicker
                oneTap
                cleanable={false}
                format="MM/dd/yyyy"
                style={{ width: '100%' }}
                value={editRateDate}
                onChange={setEditRateDate}
              />
            </div>

            <div>
              <div className="ppc-cfg-setting__label">Rate</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                precision={2}
                value={editRateValue}
                onChange={setEditRateValue}
                formatter={currencyInputFormatter}
                parser={currencyInputParser}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          left={
            <NsiButton
              variant="danger"
              loading={deleteRate.isPending}
              disabled={upsertRate.isPending || deleteRate.isPending}
              onClick={() => void deleteEditRate()}
            >
              Delete
            </NsiButton>
          }
        >
          <NsiButton
            onClick={closeEditRate}
            disabled={upsertRate.isPending || deleteRate.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={upsertRate.isPending}
            disabled={upsertRate.isPending || deleteRate.isPending}
            onClick={() => void saveEditRate()}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={addRateOpen} onClose={() => setAddRateOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Add Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Project</div>
              <SelectPicker
                cleanable={false}
                searchable
                placeholder={projectsLoading ? 'Loading projects…' : 'Select a project'}
                style={{ width: '100%' }}
                data={projectOptions}
                value={Number.isInteger(newProjectId) ? newProjectId : null}
                onChange={(v) => setNewProjectId(Number.isInteger(v) ? v : null)}
                disabled={projectsLoading || projectOptions.length === 0}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Effective date</div>
              <DatePicker
                oneTap
                style={{ width: '100%' }}
                value={newEffectiveDate}
                onChange={setNewEffectiveDate}
                format="MM/dd/yyyy"
                placeholder="Select effective date"
                cleanable={false}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Rate</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                precision={2}
                value={newRate}
                onChange={setNewRate}
                formatter={currencyInputFormatter}
                parser={currencyInputParser}
                onFocus={(e) => {
                  queueMicrotask(() => e?.target?.select?.());
                }}
                onClick={(e) => {
                  queueMicrotask(() => e?.target?.select?.());
                }}
                disabled={!Number.isInteger(newProjectId)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setAddRateOpen(false)} disabled={upsertRate.isPending}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitAddRate} loading={upsertRate.isPending}>
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
